import TelemetryTile from './TelemetryTile';
import { useGcsStore } from '../../../store/useGcsStore';
import { BatteryChargingState, type VehicleId } from '../../../types/gcs/vehicle';
import { formatPercentage, formatSpeedMetersPerSec, formatAltitudeM } from '../../../utils/formatters';
import { en } from '../../../locales/en';
import { selectVehicleStatusCategory } from '../../../store/selectors/selectVehicleStatusCategory';
import { VehicleStatusCategory } from '../../../types/ui/vehicleStatusCategory';
import {
  ArrowNarrowUp,
  BatteryCharging01,
  BatteryEmpty,
  BatteryFull,
  BatteryLow,
  BatteryMid,
  Speedometer01
} from '@untitledui/icons';
import { Icon } from '../../../components/general/Icon';

interface SidePanelVehicleTelemetryTileProps {
  gcsId: string;
  vehicleId: VehicleId;
}

export const SidePanelVehicleTelemetryTile = ({ gcsId, vehicleId }: SidePanelVehicleTelemetryTileProps) => {
  const liveMission = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;
    return gcsData.liveMissionsMap.get(vehicleId);
  });

  const isInflight = useGcsStore((state) => {
    const category = selectVehicleStatusCategory(state.data, gcsId, vehicleId);
    return (
      category === VehicleStatusCategory.INFLIGHT_NOMINAL || category === VehicleStatusCategory.INFLIGHT_FAILURE_MODE
    );
  });

  // For inflight: get data from mission flight data
  const flightData = useGcsStore((state) => {
    if (!isInflight || !liveMission) return null;
    return state.data.get(gcsId)?.missionFlightDataMap.get(liveMission.missionId);
  });

  // For on ground: get battery data from vehicle mission computer status
  const batteryChargePct = useGcsStore(
    (state) => state.data.get(gcsId)?.vehicleMissionComputerStatusMap.get(vehicleId)?.batteryChargePct
  );

  const batteryChargingState = useGcsStore(
    (state) => state.data.get(gcsId)?.vehicleMissionComputerStatusMap.get(vehicleId)?.batteryChargingState
  );

  const getBatteryIcon = (batteryPct: number | null | undefined) => {
    if (batteryChargingState === BatteryChargingState.CHARGING) {
      return BatteryCharging01;
    }

    if (batteryPct === null || batteryPct === undefined) {
      return BatteryEmpty;
    }

    if (batteryPct <= 33) {
      return BatteryLow;
    }

    if (batteryPct <= 66) {
      return BatteryMid;
    }

    return BatteryFull;
  };

  // Get battery value based on state
  let batteryValue: string = en.common.notAvailable;
  let batteryColor = undefined;

  if (isInflight && flightData) {
    batteryValue = formatPercentage(flightData.batteryPct);
  } else if (!isInflight && batteryChargePct) {
    batteryValue = formatPercentage(batteryChargePct);
    batteryColor = batteryChargingState === BatteryChargingState.ERROR ? 'var(--color-error)' : undefined;
  }

  const batteryPctForIcon = isInflight ? flightData?.batteryPct : batteryChargePct;
  const batteryIcon = getBatteryIcon(batteryPctForIcon);

  // Get speed and altitude values
  const speedValue = isInflight && flightData ? formatSpeedMetersPerSec(flightData.speedMs) : en.common.notAvailable;
  const altitudeValue = isInflight && flightData ? formatAltitudeM(flightData.location.alt) : en.common.notAvailable;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <TelemetryTile icon={<Icon icon={Speedometer01} size={16} />} value={speedValue} title={en.telemetry.speed} />
      <TelemetryTile
        icon={<Icon icon={ArrowNarrowUp} size={16} />}
        value={altitudeValue}
        title={en.telemetry.altitude}
      />
      <TelemetryTile
        icon={<Icon icon={batteryIcon} size={16} />}
        value={batteryValue}
        color={batteryColor}
        title={en.telemetry.battery}
      />
    </div>
  );
};
